import { c } from 'ttag';

import { ContactMatchEmptyReason, ContactOnboardingStatus } from '../../../graphql/__generated__/schema';

/** Step 1's two enumerations, neither re-derived here: the wording is ours, keyed on the gesture each value calls for. */

/** Why the search came back with nothing, and what to do about it. */
export interface LookupRefusal {
    title: string;
    body: string;
    tone: 'info' | 'warning' | 'danger';
}

// Three empty answers separated by the gesture. The middle one means the address is RIGHT:
// suggesting a correction would send the operator round in circles.
export const lookupRefusal = (reason: ContactMatchEmptyReason | null | undefined): LookupRefusal | null => {
    switch (reason) {
        case ContactMatchEmptyReason.NoContactForEmail:
            return {
                title: c('Onboarding lookup refusal').t`No ingested contact carries this address.`,
                body: c('Onboarding lookup refusal')
                    .t`Only known contacts can be onboarded: a person the source does not know has no account, no market and no perimeter, and would sign in to an empty portal. Re-read the address, or have the contact created in the source and search again once it has been ingested.`,
                tone: 'danger',
            };
        case ContactMatchEmptyReason.ContactsOutOfPerimeter:
            return {
                title: c('Onboarding lookup refusal').t`This address is known, but not on an account you administer.`,
                body: c('Onboarding lookup refusal')
                    .t`The address is the right one — do not correct it. Its contacts sit on accounts outside your portfolio, so neither their identity nor their company can be shown to you. Ask for the account to be added to your portfolio, or hand the onboarding over to whoever administers it.`,
                tone: 'warning',
            };
        case ContactMatchEmptyReason.NoAccountInPerimeter:
            return {
                title: c('Onboarding lookup refusal').t`You administer no account yet.`,
                body: c('Onboarding lookup refusal')
                    .t`No address will return anything until at least one account is entrusted to you — this is about your portfolio, not about the address you typed. Ask an administrator for a portfolio, then search again.`,
                tone: 'warning',
            };
        default:
            return null;
    }
};

/**
 * What a contact's onboarding status means for the journey. selectable comes from the API's scale.
 * ONBOARDED is not a refusal: the journey becomes "add accounts" on an existing person.
 */
export interface ContactStatusReading {
    tag: string;
    meaning?: string;
    selectable: boolean;
    tone: 'info' | 'warning' | 'danger';
}

export const contactStatusReading = (status: ContactOnboardingStatus): ContactStatusReading => {
    switch (status) {
        case ContactOnboardingStatus.Eligible:
            return {
                tag: c('Onboarding contact status').t`Can be onboarded`,
                meaning: c('Onboarding contact status')
                    .t`Nothing on the source's side refuses this contact: the journey can go on to the accounts.`,
                selectable: true,
                tone: 'info',
            };
        case ContactOnboardingStatus.Onboarded:
            // A bifurcation, not a refusal: blocking would prevent widening an existing person's perimeter.
            return {
                tag: c('Onboarding contact status').t`Already onboarded`,
                selectable: true,
                tone: 'info',
            };
        case ContactOnboardingStatus.LeftCompany:
            return {
                tag: c('Onboarding contact status').t`Left the company`,
                meaning: c('Onboarding contact status')
                    .t`The source declares this person as having left. Onboarding them would open an access to somebody who is no longer there — if they have in fact come back, the source has to say so first. Note that this refuses a new onboarding only: a person already onboarded keeps their access, whose revocation stays a traced human decision.`,
                selectable: false,
                tone: 'danger',
            };
        case ContactOnboardingStatus.NoAccount:
            return {
                tag: c('Onboarding contact status').t`Attached to no account`,
                meaning: c('Onboarding contact status')
                    .t`This contact carries no live attachment, so there is no perimeter to grant: the portal's data perimeter rests on accounts. Attach them to an account in the source, then search again. The address is known and correct — it is the attachment that is missing.`,
                selectable: false,
                tone: 'danger',
            };
        case ContactOnboardingStatus.Vanished:
            return {
                tag: c('Onboarding contact status').t`Gone from the source`,
                meaning: c('Onboarding contact status')
                    .t`This contact no longer exists on the source's side, so nothing should be onboarded on that basis — the person has no account there any more, hence no perimeter. Existing accesses are not revoked by this: that stays a traced human decision.`,
                selectable: false,
                tone: 'danger',
            };
        case ContactOnboardingStatus.NoEmail:
            // Unreachable through this query (searches by address), but worded rather than folded
            // into a default, so another lookup returning it would not be a silent refusal.
            return {
                tag: c('Onboarding contact status').t`No email address`,
                meaning: c('Onboarding contact status')
                    .t`This contact carries no email address in the source, and the address is what carries the invitation and becomes the sign-in identifier. Fill it in the source before onboarding them.`,
                selectable: false,
                tone: 'danger',
            };
    }
};

/** The full name as the source writes it, or null when it holds neither half. */
export const contactFullName = (contact: { firstName?: string | null; lastName?: string | null }): string | null =>
    [contact.firstName, contact.lastName].filter(Boolean).join(' ') || null;
